import {
  Autocomplete,
  Box,
  Chip,
  FormControlLabel,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { API_URL, rpl_status } from "const/constants";
import useKothar from "context/useKothar";
import { useEffect, useRef, useState } from "react";
import { IoArrowBack } from "react-icons/io5";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import AlertModal from "./AlertModal";
import DiscussionTab from "./components/DiscussionTab";
import DocumentsTab from "./components/DocumentsTab";
import OverviewTab from "./components/OverviewTab";
import PaymentsTab from "./components/PaymentsTab";
import TimelineTab from "./components/TimelineTab";
import ModuleTasksTab from "components/Tasks/ModuleTasksTab";
import { usePreselectClientFromLocation } from "../shared/moduleClients/usePreselectClientFromLocation";
import ModuleLockedTab from "../shared/ModuleLockedTab";
import { defaultGstSetting } from "utils/gstCalc";
import { promptReferralShare } from "../Clients/ReferralSharePrompt";

const AddRPLCertificate = ({ color = "light" }) => {
  const [data, setData] = useState({
    name: null,
    address: null,
    dob: null,
    gender: null,
    email: null,
    number: null,
    usiNumber: null,
    visaStatus: null,
    visaExpiry: "",
    passport: "",
    visa: "",
    coe: [],
    completeStatus: null,
    status: null,
    previousStatus: null,
    certificate: "",
    currentlyEnrolledCourse: "",
    currentlyEnrolledUniversity: "",
    courseEndDate: "",
    placementRequired: null,
    caseOfficer: "",
    reference: "",
    cost: "",
    serviceCharge: "",
    extraCharges: "",
    kotharCost: "",
    discount: "",
    costGst: defaultGstSetting(false),
    serviceChargeGst: defaultGstSetting(true),
    extraChargesGst: defaultGstSetting(false),
    kotharCostGst: defaultGstSetting(false),
    payments: [],
    siteName: "",
    placementStartDate: "",
  });

  const [{ rplList, token, usersList }, { getRPLList, refetchUsers }] = useKothar();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!usersList?.length) refetchUsers();
  }, [usersList?.length, refetchUsers]);
  const [openAlertModal, setOpenAlertModal] = useState({
    state: false,
    message: "",
    data: null,
  });

  // Tab state
  const [searchParams] = useSearchParams();
  const [currentTab, setCurrentTab] = useState(() => {
    const fromQuery = {
      details: 0,
      timeline: 1,
      documents: 2,
      payments: 3,
      discussion: 4,
      tasks: 5,
    }[searchParams.get("tab")];
    return fromQuery ?? 0;
  });

  // Timeline states
  // Discussion states
  const [commentsList, setCommentsList] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionSearchTerm, setMentionSearchTerm] = useState("");
  const [mentionStartIndex, setMentionStartIndex] = useState(-1);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const chatContainerRef = useRef(null);

  const navigate = useNavigate();
  const userDetail = JSON.parse(localStorage.getItem("userDetail"));
  const { state } = useLocation();
  const { id } = useParams();

  usePreselectClientFromLocation({ setData, editId: id });

  const { data: fetchedRpl } = useQuery(
    ["rplDetail", id],
    async () => {
      const res = await axios.get(`${API_URL}/rpl/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      return res?.data?.data ?? null;
    },
    { enabled: !!id, staleTime: 0 }
  );

  // Server payload first (form draft hydrates from it); the tab badge shows only when > 0.
  const openRplTaskCount = Number(fetchedRpl?.openTaskCount ?? data?.openTaskCount ?? 0);

  const normalizeRplToForm = (item) => ({
    ...item,
    previousStatus: item?.status,
    placementRequired:
      item?.placementRequired === true ? "yes" : item?.placementRequired === false ? "no" : null,
    insuranceStudentCard:
      item?.insuranceStudentCard === true
        ? "yes"
        : item?.insuranceStudentCard === false
        ? "no"
        : null,
  });

  useEffect(() => {
    if (fetchedRpl) setData(normalizeRplToForm(fetchedRpl));
  }, [fetchedRpl]);

  // Initialize data from navigation preselect (only for a brand-new record — for edits
  // we always prefer the freshly-fetched server record so a page refresh shows current data).
  useEffect(() => {
    if (!state || id) return;
    setData({
      ...state?.item,
      previousStatus: state?.item?.status,
      placementRequired:
        state?.item?.placementRequired === true
          ? "yes"
          : state?.item?.placementRequired === false
          ? "no"
          : null,
      insuranceStudentCard:
        state?.item?.insuranceStudentCard === true
          ? "yes"
          : state?.item?.insuranceStudentCard === false
          ? "no"
          : null,
    });
  }, [state, id]);

  const fetchDiscussions = async (id) => {
    await axios
      .get(`${API_URL}/discussion/${id}?type=rpl`)
      .then((res) => {
        setCommentsList(res?.data?.comments?.reverse());
        chatContainerRef?.current?.scrollIntoView();
      })
      .catch((err) => console.log(err));
  };

  useEffect(() => {
    if (data?.clientId) {
      fetchDiscussions(data?.clientId);
    }
  }, [data?.clientId]);

  // Filter users for mentions
  useEffect(() => {
    if (mentionSearchTerm && usersList) {
      const filtered = usersList.filter(
        (user) =>
          user.name?.toLowerCase().includes(mentionSearchTerm.toLowerCase()) ||
          user.email?.toLowerCase().includes(mentionSearchTerm.toLowerCase())
      );
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers([]);
    }
  }, [mentionSearchTerm, usersList]);

  // Post comment mutation
  const { mutate: postComment } = useMutation(
    async (payload) => {
      await axios.post(`${API_URL}/discussion/${data?.clientId}`, payload);
    },
    {
      onSuccess: () => {
        if (data?.clientId) {
          fetchDiscussions(data.clientId);
        }
        setCommentText("");
      },
      onError: () => {
        toast.error("Error Submitting Comment");
      },
    }
  );

  // Submit data mutation
  const { mutate: mutateData, isLoading } = useMutation(
    async (payload) => {
      if (payload?.id) {
        return await axios.put(`${API_URL}/rpl/${payload.id}`, payload);
      } else {
        return await axios.post(`${API_URL}/rpl`, payload);
      }
    },
    {
      onSuccess: (response) => {
        toast.success(data?.id ? "Data updated Successfully" : "Data added Successfully");
        const saved = response?.data?.data;
        if (!data?.id && saved?.id) {
          promptReferralShare({
            module: "rpl-course",
            entryId: saved.id,
            clientId: saved?.clientId ?? data?.clientId,
          });
        }
        if (saved) {
          setData(normalizeRplToForm(saved));
        } else if (!data?.id && response?.data?.data?.id) {
          setData((prev) => ({ ...prev, id: response.data.data.id }));
        }
        queryClient.invalidateQueries(["rplPage"]);
        if (id) queryClient.invalidateQueries(["rplDetail", id]);
        queryClient.invalidateQueries(["rpl-clients-page"]);
        if (saved?.clientId != null) {
          queryClient.invalidateQueries(["rpl-client-records", String(saved.clientId)]);
        }
      },
      onError: () => {
        toast.error(data?.id ? "Error Updating Data" : "Error Submitting Data");
      },
    }
  );

  // Handle status change
  const handleStatusChange = (event, newValue) => {
    if (newValue?.value) {
      setData((prev) => ({
        ...prev,
        previousStatus: prev.status,
        status: newValue.value,
      }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle submit
  const handleSubmit = (e) => {
    e.preventDefault();
    const submitData = {
      ...data,
      useExistingClientData: true,
      placementRequired:
        data.placementRequired === "yes" ? true : data.placementRequired === "no" ? false : null,
      insuranceStudentCard:
        data.insuranceStudentCard === "yes"
          ? true
          : data.insuranceStudentCard === "no"
          ? false
          : null,
    };
    mutateData(submitData);
  };

  // Comment handlers
  const handleCommentSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (commentText.trim() && data?.id) {
      postComment({ text: commentText, type: "rpl" });
    }
  };

  const handleCommentTextChange = (e) => {
    const value = e.target.value;
    setCommentText(value);
    const lastAtIndex = value.lastIndexOf("@");
    if (lastAtIndex !== -1) {
      const searchTerm = value.substring(lastAtIndex + 1);
      if (!searchTerm.includes(" ") && searchTerm.length > 0) {
        setMentionSearchTerm(searchTerm);
        setMentionStartIndex(lastAtIndex);
        setShowMentionDropdown(true);
        setSelectedMentionIndex(0);
      } else {
        setShowMentionDropdown(false);
      }
    } else {
      setShowMentionDropdown(false);
    }
  };

  const handleMentionSelect = (user) => {
    const beforeMention = commentText.substring(0, mentionStartIndex);
    const afterMention = commentText.substring(mentionStartIndex + mentionSearchTerm.length + 1);
    const newText = `${beforeMention}@${user.email} ${afterMention}`;
    setCommentText(newText);
    setShowMentionDropdown(false);
    setMentionSearchTerm("");
    setMentionStartIndex(-1);
  };

  const handleCommentKeyDown = (e) => {
    if (showMentionDropdown) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedMentionIndex((prev) => (prev < filteredUsers.length - 1 ? prev + 1 : 0));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedMentionIndex((prev) => (prev > 0 ? prev - 1 : filteredUsers.length - 1));
      } else if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        if (filteredUsers[selectedMentionIndex]) {
          handleMentionSelect(filteredUsers[selectedMentionIndex]);
        }
      } else if (e.key === "Escape") {
        setShowMentionDropdown(false);
      }
    }
  };

  const deleteComment = (commentId) => {
    axios({
      url: `${API_URL}/discussion/comment/${commentId}`,
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    })
      .then(() => {
        toast.success("Comment Deleted Successfully");
        fetchDiscussions(data?.id);
      })
      .catch(() => {
        toast.error("Error Deleting Comment");
      });
  };

  const renderTextWithMentions = (text) => {
    if (!text) return "";
    const parts = text.split(/(@\S+)/g);
    return parts.map((part, index) => {
      if (part.startsWith("@")) {
        const mentionText = part.substring(1);
        let user = usersList?.find((u) => u.email === mentionText);
        if (!user) {
          user = usersList?.find((u) => u.name === mentionText);
        }
        if (user) {
          return (
            <span key={index} className="text-primary font-semibold bg-blue-100 px-1 rounded">
              @{user.name}
            </span>
          );
        }
      }
      return <span key={index}>{part}</span>;
    });
  };

  const tabPanels = [
    <OverviewTab
      key="overview"
      data={data}
      setData={setData}
      rplList={rplList}
      id={id}
      setOpenAlertModal={setOpenAlertModal}
      handleInputChange={handleInputChange}
      isLoading={isLoading}
      navigate={navigate}
    />,
    <TimelineTab key="timeline" data={data} timelineEvents={[]} />,
    <DocumentsTab
      key="documents"
      data={data}
      setData={setData}
      isLoading={isLoading}
      navigate={navigate}
    />,
    <PaymentsTab
      key="payments"
      data={data}
      setData={setData}
      isLoading={isLoading}
      navigate={navigate}
      usersList={usersList}
    />,
    <DiscussionTab
      key="discussion"
      commentsList={commentsList}
      commentText={commentText}
      setCommentText={setCommentText}
      showMentionDropdown={showMentionDropdown}
      filteredUsers={filteredUsers}
      selectedMentionIndex={selectedMentionIndex}
      handleCommentTextChange={handleCommentTextChange}
      handleCommentKeyDown={handleCommentKeyDown}
      handleMentionSelect={handleMentionSelect}
      handleCommentSubmit={handleCommentSubmit}
      renderTextWithMentions={renderTextWithMentions}
      deleteComment={deleteComment}
      userDetail={userDetail}
      data={data}
      chatContainerRef={chatContainerRef}
    />,
    <div key="tasks" className="py-2">
      {data?.id ? (
        <ModuleTasksTab
          entryType="RPL"
          entryId={data.id}
          clientId={data?.clientId}
          clientName={data?.name}
          taskModule="RPL"
          assignTitle={`RPL: ${data?.certificate || "RPL certificate"} (${data?.name || "client"})`}
          onTasksChanged={() => {
            if (id) queryClient.invalidateQueries(["rplDetail", id]);
          }}
        />
      ) : (
        <p className="text-sm text-app-text-muted text-center py-6 m-0">
          Save the RPL certificate first to start tracking tasks.
        </p>
      )}
    </div>,
  ];

  return (
    <div className="flex flex-wrap mt-4 dashBody">
      <div className="w-full mb-12 px-4">
        <div
          className={`relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded ${"bg-app-surface text-app-text"}`}
        >
          <form className="flex flex-col space-y-4 md:space-y-6" onSubmit={handleSubmit}>
            {/* Header */}
            <div className="rounded-t mb-0 px-6 py-4 border-b border-app-border">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                  <IoArrowBack className="text-xl cursor-pointer" onClick={() => navigate(-1)} />
                  <h3 className={`font-semibold text-xl ${"text-app-text"}`}>
                    {data?.id ? "RPL Certificate Details of " + data?.name : "Add RPL Certificate"}
                  </h3>
                  {data?.migrated && (
                    <Tooltip title="Migrated from legacy data — complete the missing details">
                      <Chip
                        label="Migrated"
                        size="small"
                        color="warning"
                        variant="outlined"
                        sx={{ fontSize: 9, height: 18 }}
                      />
                    </Tooltip>
                  )}
                </div>

                <div className="flex items-center gap-4 flex-wrap">
                  <FormControlLabel
                    control={
                      <Switch
                        sx={{ m: 0 }}
                        onChange={(e) => {
                          setData({
                            ...data,
                            completeStatus: e.target.checked,
                          });
                        }}
                        checked={data?.completeStatus}
                      />
                    }
                    label={`${data?.completeStatus ? "Complete" : "Incomplete"}`}
                    className={`${data?.completeStatus ? "text-green-500" : "text-red-500"}`}
                  />
                  <Autocomplete
                    size="small"
                    sx={{ width: 250 }}
                    required
                    value={rpl_status?.find((item) => item?.value === data?.status) || null}
                    options={rpl_status}
                    disablePortal
                    renderInput={(params) => (
                      <TextField {...params} label="Select RPL Status" required />
                    )}
                    ListboxProps={{ style: { maxHeight: "180px" } }}
                    onChange={handleStatusChange}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                  />
                </div>
              </div>
            </div>

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: "divider", px: 3 }}>
              <Tabs
                value={currentTab}
                onChange={(e, newValue) => setCurrentTab(newValue)}
                aria-label="RPL certificate tabs"
              >
                <Tab label="Overview" />
                <ModuleLockedTab
                  label="Timeline"
                  locked={!data?.id}
                  lockMessage="Create the RPL certificate first to unlock this tab"
                />
                <ModuleLockedTab
                  label={`Documents (${data?.coe?.length || 0})`}
                  locked={!data?.id}
                  lockMessage="Create the RPL certificate first to unlock this tab"
                />
                <ModuleLockedTab
                  label="Payments"
                  locked={!data?.id}
                  lockMessage="Create the RPL certificate first to unlock this tab"
                />
                <ModuleLockedTab
                  label="Discussion"
                  locked={!data?.id}
                  lockMessage="Create the RPL certificate first to unlock this tab"
                />
                <ModuleLockedTab
                  label={openRplTaskCount > 0 ? `Tasks (${openRplTaskCount})` : "Tasks"}
                  locked={!data?.id}
                  lockMessage="Create the RPL certificate first to unlock this tab"
                />
              </Tabs>
            </Box>

            {/* Tab Content */}
            <div className="px-6 py-0">{tabPanels[currentTab]}</div>
          </form>
        </div>
      </div>

      {/* Alert Modal */}
      {openAlertModal?.state && (
        <AlertModal
          open={openAlertModal?.state}
          onClose={() => setOpenAlertModal({ state: false, message: "", data: null })}
          message={openAlertModal?.message}
          data={openAlertModal?.data}
        />
      )}
    </div>
  );
};

export default AddRPLCertificate;
